import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import SampleCategory from '../../../types/sample-category/SampleCategory';
import SampleCategoryWriteDTO from '../../../types/sample-category/SampleCategoryWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class SampleCategoryService {

    private RESOURCE: string = 'sample-category';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the sample categories from the backend.
     *
     * @param search The search query to filter the sample categories by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The sample categories.
     */
    public async getSampleCategories(search?: string, page: number = 0,
                                     size: number = 10): Promise<Page<SampleCategory>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<SampleCategory>>(this.RESOURCE, params);
    }

    /**
     * Fetches sample categories by sample type ID.
     *
     * @param sampleTypeId The ID of the sample type.
     * @return The sample categories.
     */
    public async getSampleCategoriesByType(sampleTypeId: number): Promise<SampleCategory[]> {
        return await this.baseService.get<SampleCategory[]>(`${ this.RESOURCE }/type/${ sampleTypeId }`);
    }

    /**
     * Fetches all sample categories from the backend.
     *
     * @return The sample categories.
     */
    public async getAllSampleCategories(): Promise<SampleCategory[]> {
        return await this.baseService.get<SampleCategory[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches sample categories by detail ID.
     * @param detailId The ID of the detail.
     * @return The sample categories.
     */
    public async getSampleCategoriesByDetailId(detailId: number): Promise<SampleCategory[]> {
        return await this.baseService.get<SampleCategory[]>(`${ this.RESOURCE }/detail/${ detailId }`);
    }

    /**
     * Add details to a category.
     * @param categoryId The ID of the category.
     * @param detailIds The IDs of the details to add.
     * @return The response from the request.
     */
    public async addDetailsToCategory(categoryId: number, detailIds: number[]): Promise<void> {
        return await this.baseService.post<void>(`${ this.RESOURCE }/${ categoryId }/add-details`, detailIds);
    }

    /**
     * Fetches a sample category from the backend.
     *
     * @param id The ID of the sample category to fetch.
     * @return The sample category.
     */
    public async getSampleCategory(id: number): Promise<SampleCategory> {
        return await this.baseService.get<SampleCategory>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a sample category in the backend.
     *
     * @param sampleCategory The sample category to create.
     * @return The response from the request.
     */
    public async createSampleCategory(sampleCategory: SampleCategoryWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, sampleCategory);
    }

    /**
     * Updates a sample category in the backend.
     *
     * @param sampleCategory The sample category to update.
     * @return The response from the request.
     */
    public async updateSampleCategory(sampleCategory: SampleCategoryWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + sampleCategory.id, sampleCategory);
    }

    /**
     * Deletes a sample category from the backend.
     *
     * @param id The ID of the sample category to delete.
     * @return The response from the request.
     */
    public async deleteSampleCategory(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
